import { Fragment } from 'react';
import { Separator } from '@/app/_components/core/separator';
import type { FabCta } from '@/app/_components/shared/fab/fab.types';
import { cn } from '@/app/_lib/utils';
import { FabContactItem } from './fab-contact-item';

interface FabPanelContentProps {
  headline?: string;
  subline?: string;
  copytext?: string;
  ctas: FabCta[];
  footerNote?: string;
  onItemClick: () => void;
  panelTitleId?: string;
}

// =============================================================================
// Class names — split into Tailwind defaults and design-token classes
// =============================================================================

// Header: eyebrow line above the title (brand-coloured)
const getDefaultSublineClasses = () => 'text-sm font-medium leading-none';
const getTokenSublineClasses = () => 'text-primary';

// Header: title
const getDefaultHeadlineClasses = () => 'text-lg font-semibold leading-none';
const getTokenHeadlineClasses = () => 'text-foreground';

// Header: description below the title
const getDefaultCopytextClasses = () => 'text-sm font-normal';
const getTokenCopytextClasses = () => 'text-muted-foreground';

// Footer note (e.g. opening hours)
const getDefaultFooterNoteClasses = () => 'p-4 text-center text-sm font-normal';
const getTokenFooterNoteClasses = () => 'text-muted-foreground';

/**
 * Panel content (header + items list + footer) — used inside Popover (desktop)
 * and Drawer (mobile). Layout-only — animations + portal handled by parent.
 */
export function FabPanelContent({
  headline,
  subline,
  copytext,
  ctas,
  footerNote,
  onItemClick,
  panelTitleId,
}: FabPanelContentProps) {
  const hasHeader = [subline, headline, copytext].some(Boolean);

  return (
    <div className="flex flex-col">
      {hasHeader && (
        <header className="flex flex-col gap-1.5 p-4">
          {subline && (
            <p className={cn(getDefaultSublineClasses(), getTokenSublineClasses())}>
              {subline}
            </p>
          )}
          {headline && (
            <h2
              id={panelTitleId}
              className={cn(getDefaultHeadlineClasses(), getTokenHeadlineClasses())}
            >
              {headline}
            </h2>
          )}
          {copytext && (
            <p className={cn(getDefaultCopytextClasses(), getTokenCopytextClasses())}>
              {copytext}
            </p>
          )}
        </header>
      )}

      {hasHeader && <Separator spacing="none" />}

      <ul className="flex flex-col">
        {ctas.map((cta, idx) => (
          <Fragment key={`${cta.linkUrl}-${idx}`}>
            {idx > 0 && (
              <li role="separator" aria-hidden="true">
                <Separator spacing="none" />
              </li>
            )}
            <li>
              <FabContactItem
                cta={cta}
                onNavigate={onItemClick}
                trackingPrefix={`fab-contact-${idx + 1}`}
              />
            </li>
          </Fragment>
        ))}
      </ul>

      {footerNote && (
        <>
          <Separator spacing="none" />
          <p className={cn(getDefaultFooterNoteClasses(), getTokenFooterNoteClasses())}>
            {footerNote}
          </p>
        </>
      )}
    </div>
  );
}
